import { useEffect, useState } from "react";
import { Card } from "./Layout";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { apiError, http, SEDES } from "../lib/api";

const SEDE_LIST = Object.entries(SEDES);

/** Calendario semanal de Logopedia por sede, editable desde el panel del equipo. */
export const LogopediaCalendar = ({ patients, onChanged }) => {
  const [sede, setSede] = useState("xalo");
  const [grid, setGrid] = useState(null);
  const [cell, setCell] = useState(null); // {day, start} hueco pulsado
  const [pick, setPick] = useState("");
  const [editing, setEditing] = useState(null); // paciente pulsado
  const [form, setForm] = useState({ day: "", start: "", sede: "" });
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const load = async (s = sede) => {
    const { data } = await http.get("/admin/logopedia/schedule", { params: { sede: s } });
    setGrid(data);
  };

  useEffect(() => { load(sede).catch((err) => setError(apiError(err))); }, [sede]);

  const run = async (fn, okMsg) => {
    setMsg(""); setError("");
    try {
      await fn();
      setMsg(okMsg);
      await load();
      if (onChanged) await onChanged();
      setCell(null); setPick(""); setEditing(null);
    } catch (err) { setError(apiError(err)); }
  };

  // Horario fijo nuevo del paciente: se sustituye el día/hora elegido y se conserva el resto.
  const assign = (patient, day, start, toSede) => {
    const current = (patient.fixed_schedule || []).map((f) => ({ day: f.day, start: f.start }));
    const sessions = patient.bono_total || 4;
    let next;
    if (sessions >= 8) {
      const rest = current.filter((f) => !(f.day === day)).slice(0, 1);
      next = [...rest, { day, start }];
    } else {
      next = [{ day, start }];
    }
    if (toSede && toSede !== patient.sede) {
      return http.put(`/admin/logopedia/patients/${patient.id}/sede`,
        { sede: toSede, fixed_schedule: next });
    }
    return http.put(`/admin/logopedia/patients/${patient.id}/schedule`, { fixed_schedule: next });
  };

  if (!grid) {
    return (
      <Card>
        <p className="text-lg text-ink-700">Cargando el calendario…</p>
        {error && <p data-testid="lcal-error" className="mt-4 rounded-xl bg-red-50 p-4 text-base text-red-700">{error}</p>}
      </Card>
    );
  }

  const sedePatients = patients.filter((p) => p.sede === sede);

  return (
    <div className="space-y-6" data-testid="logopedia-calendar">
      <div className="flex flex-wrap gap-3">
        {SEDE_LIST.map(([k, l]) => (
          <Button key={k} data-testid={`lcal-sede-${k}`} variant={sede === k ? "default" : "outline"}
            className="h-12 text-base" onClick={() => { setSede(k); setCell(null); setEditing(null); }}>
            Sede de {l}
          </Button>
        ))}
      </div>

      <Card>
        <h2 className="font-head text-2xl font-medium">Horario semanal de Logopedia — {grid.sede_label}</h2>
        <p className="mt-2 text-base text-ink-700">
          Pulse un hueco libre para asignar un paciente, o el nombre de un paciente para cambiarle el
          día, la hora o la sede. Las sesiones ya realizadas no se modifican nunca.
        </p>
        {msg && <p data-testid="lcal-success" className="mt-4 rounded-xl bg-logo-light p-4 text-base text-logo-text">{msg}</p>}
        {error && <p data-testid="lcal-error" className="mt-4 rounded-xl bg-red-50 p-4 text-base text-red-700">{error}</p>}

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[640px] border-separate border-spacing-2">
            <thead>
              <tr>
                <th className="w-20 text-left text-base font-medium text-ink-700">Hora</th>
                {grid.days.map((d) => (
                  <th key={d.day} className="font-head text-lg font-medium">{d.day_name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.times.map((t) => (
                <tr key={t}>
                  <th className="text-left font-head text-base font-medium text-ink-800">{t}</th>
                  {grid.days.map((d) => {
                    const slot = d.times.find((x) => x.start === t);
                    const busy = slot.patients;
                    return (
                      <td key={d.day} className="align-top">
                        <div className="space-y-2">
                          {busy.map((p) => (
                            <button key={p.patient_id} type="button"
                              data-testid={`lcal-patient-${d.day}-${t}-${p.patient_id}`}
                              onClick={() => {
                                setEditing({ ...p, day: d.day, start: t });
                                setForm({ day: String(d.day), start: t, sede });
                                setCell(null);
                              }}
                              className="w-full rounded-xl border-2 border-logo bg-logo-light p-3 text-left transition-colors hover:border-logo-text">
                              <p className="text-base font-medium">{p.name}</p>
                              <p className="text-sm text-ink-700">
                                Consulta {p.room}{p.paused ? " · en pausa" : ""}
                              </p>
                            </button>
                          ))}
                          {slot.free_rooms > 0 && (
                            <button type="button" data-testid={`lcal-free-${d.day}-${t}`}
                              onClick={() => { setCell({ day: d.day, start: t }); setEditing(null); setPick(""); }}
                              className="w-full rounded-xl border border-dashed border-border bg-muted/40 p-3 text-sm text-ink-700 transition-colors hover:border-logo">
                              Libre ({slot.free_rooms}) · asignar
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {cell && (
        <Card data-testid="lcal-assign-card">
          <h3 className="font-head text-xl font-medium">
            Asignar paciente · {grid.days.find((d) => d.day === cell.day)?.day_name} a las {cell.start}
          </h3>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-base">Paciente de {grid.sede_label}</Label>
              <select data-testid="lcal-assign-select" value={pick}
                className="mt-2 h-12 min-w-[260px] rounded-lg border border-border bg-white px-2 text-base"
                onChange={(e) => setPick(e.target.value)}>
                <option value="">Elegir paciente…</option>
                {sedePatients.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · bono de {p.bono_total || 4} sesiones
                  </option>
                ))}
              </select>
            </div>
            <Button data-testid="lcal-assign-btn" className="h-12 text-base" disabled={!pick}
              onClick={() => {
                const p = patients.find((x) => x.id === pick);
                run(() => assign(p, cell.day, cell.start, sede),
                  `${p.name} queda con horario fijo en ese día y hora. Sus sesiones futuras se han reprogramado.`);
              }}>
              Asignar a este hueco
            </Button>
            <Button variant="outline" className="h-12 text-base" onClick={() => setCell(null)}>
              Cancelar
            </Button>
          </div>
        </Card>
      )}

      {editing && (
        <Card data-testid="lcal-edit-card">
          <h3 className="font-head text-xl font-medium">Cambiar el horario de {editing.name}</h3>
          <p className="mt-2 text-base text-ink-700">
            Ahora viene {grid.days.find((d) => d.day === editing.day)?.day_name} a las {editing.start}.
            Solo se reprograman las sesiones futuras.
          </p>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-base">Sede</Label>
              <select data-testid="lcal-edit-sede" value={form.sede}
                className="mt-2 h-12 rounded-lg border border-border bg-white px-2 text-base"
                onChange={(e) => setForm({ ...form, sede: e.target.value, day: "" })}>
                {SEDE_LIST.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-base">Día</Label>
              <select data-testid="lcal-edit-day" value={form.day}
                className="mt-2 h-12 rounded-lg border border-border bg-white px-2 text-base"
                onChange={(e) => setForm({ ...form, day: e.target.value })}>
                <option value="">Elegir…</option>
                {(form.sede === sede ? grid.days : []).map((d) => (
                  <option key={d.day} value={d.day}>{d.day_name}</option>
                ))}
                {form.sede !== sede && [1, 2, 3, 4].map((d) => (
                  <option key={d} value={d}>{["Lunes", "Martes", "Miércoles", "Jueves"][d - 1]}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-base">Hora</Label>
              <select data-testid="lcal-edit-start" value={form.start}
                className="mt-2 h-12 rounded-lg border border-border bg-white px-2 text-base"
                onChange={(e) => setForm({ ...form, start: e.target.value })}>
                {grid.times.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <Button data-testid="lcal-edit-save" className="h-12 text-base"
              disabled={!form.day || !form.start}
              onClick={() => {
                const p = patients.find((x) => x.id === editing.patient_id);
                run(() => assign(p, Number(form.day), form.start, form.sede),
                  `Horario de ${editing.name} actualizado. El historial de sesiones anteriores se conserva.`);
              }}>
              Guardar el horario nuevo
            </Button>
            <Button data-testid="lcal-edit-cancel" variant="outline" className="h-12 text-base"
              onClick={() => setEditing(null)}>
              Cancelar
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

/** Lista de sesiones de la sede (el antiguo calendario, ahora como apartado de detalle). */
export const SessionNotes = ({ sessions, onSave }) => (
  <Card>
    <h2 className="mb-4 font-head text-2xl font-medium">Sesiones reservadas y material</h2>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-left text-base" data-testid="lsessions-table">
        <thead>
          <tr className="border-b border-border text-ink-700">
            <th className="py-3 pr-4">Fecha</th>
            <th className="py-3 pr-4">Hora</th>
            <th className="py-3 pr-4">Consulta</th>
            <th className="py-3 pr-4">Paciente</th>
            <th className="py-3 pr-4">Estado</th>
            <th className="py-3 pr-4">Profesional</th>
            <th className="py-3">Material a aportar</th>
          </tr>
        </thead>
        <tbody>
          {sessions.length === 0 && (
            <tr><td colSpan={7} className="py-4 text-ink-700">Sin sesiones todavía.</td></tr>
          )}
          {sessions.map((s) => (
            <tr key={s.id} className="border-b border-border" data-testid={`lsession-row-${s.id}`}>
              <td className="py-3 pr-4">{s.date}</td>
              <td className="py-3 pr-4">{s.start}</td>
              <td className="py-3 pr-4">Consulta {s.room}</td>
              <td className="py-3 pr-4">{s.patient_name}</td>
              <td className="py-3 pr-4">{s.status}</td>
              <td className="py-3 pr-4">
                <div className="flex gap-2">
                  <Input data-testid={`prof-input-${s.id}`} defaultValue={s.professional || ""}
                    className="h-12 min-w-[140px] text-base" id={`prof-${s.id}`} />
                  <Button data-testid={`prof-save-${s.id}`} variant="outline" className="h-12 text-sm"
                    onClick={() => onSave(`/admin/logopedia/sessions/${s.id}/professional`,
                      { professional: document.getElementById(`prof-${s.id}`).value },
                      "Profesional guardado.")}>
                    Guardar
                  </Button>
                </div>
              </td>
              <td className="py-3">
                <div className="flex gap-2">
                  <Input data-testid={`bring-input-${s.id}`} defaultValue={s.bring_note || ""}
                    className="h-12 min-w-[160px] text-base" id={`bring-${s.id}`} />
                  <Button data-testid={`bring-save-${s.id}`} variant="outline" className="h-12 text-sm"
                    onClick={() => onSave(`/admin/logopedia/sessions/${s.id}/bring-note`,
                      { note: document.getElementById(`bring-${s.id}`).value },
                      "Aviso de material guardado.")}>
                    Guardar
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);
